import { fromHtml } from "hast-util-from-html";
import { Fragment } from "prosemirror-model";
import { trimLines } from "trim-lines";
import { visit } from "unist-util-visit";
import { zwitch } from "zwitch";
export function createState(schema, handlers, htmlHandlers, tree) {
    const definitionById = new Map();
    const footnoteById = new Map();
    const footnoteCounts = new Map();
    const state = {
        all,
        definitionById,
        footnoteById,
        footnoteCounts,
        footnoteOrder: [],
        one
    };
    visit(tree, function(node) {
        if (node.type === "definition" || node.type === "footnoteDefinition") {
            const map = node.type === "definition" ? definitionById : footnoteById;
            const id = String(node.identifier).toUpperCase();
            // Mimick CM behavior of link definitions.
            // See: <https://github.com/syntax-tree/mdast-util-definitions/blob/9032189/lib/index.js#L20-L21>.
            if (!map.has(id)) {
                // @ts-expect-error: node type matches map.
                map.set(id, node);
            }
        }
    });
    return state;
    /**
   * Transform an mdast node into a hast node.
   */ function one(node, parent) {
        return handle(schema, handlers, htmlHandlers, node, parent, state);
    }
    /**
   * Transform the children of an mdast node into hast nodes.
   */ function all(parent) {
        const values = [];
        if ("children" in parent) {
            const nodes = parent.children;
            let index = -1;
            while(++index < nodes.length){
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                let result = state.one(nodes[index], parent);
                // To do: see if we van clean this? Can we merge texts?
                if (result) {
                    if (index && nodes[index - 1]?.type === "break") {
                        if (!Array.isArray(result) && result.type === schema.nodes["text"]) {
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            result = schema.text(trimMarkdownSpaceStart(result.text));
                        }
                        if (!Array.isArray(result) && result.type !== schema.nodes["text"]) {
                            const head = result.firstChild;
                            if (head && head.type === schema.nodes["text"]) {
                                result = result.copy(Fragment.from(result.content).replaceChild(0, // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                schema.text(trimMarkdownSpaceStart(head.text))));
                            }
                        }
                    }
                    if (Array.isArray(result)) {
                        values.push(...result);
                    } else {
                        values.push(result);
                    }
                }
            }
        }
        return values;
    }
}
function replaceNewlines(text) {
    return text.replaceAll(/\n([^\n])/g, " $1");
}
function revert(schema, node, state) {
    const subtype = node.referenceType;
    let suffix = "]";
    if (subtype === "collapsed") {
        suffix += "[]";
    } else if (subtype === "full") {
        // We actually want to fall back if node.label is falsy, not just when
        // it's nullish
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        suffix += "[" + (node.label || node.identifier) + "]";
    }
    if (node.type === "imageReference") {
        return [
            schema.text("![" + (node.alt ?? "") + suffix)
        ];
    }
    const contents = state.all(node);
    const head = contents[0];
    if (head && head.type === schema.nodes["text"]) {
        contents[0] = head.copy(Fragment.from(schema.text("[" + (head.text ?? ""))));
    } else {
        contents.unshift(schema.text("["));
    }
    const tail = contents[contents.length - 1];
    if (tail && tail.type === schema.nodes["text"]) {
        contents[contents.length - 1] = tail.copy(Fragment.from(schema.text((tail.text ?? "") + suffix)));
    } else {
        contents.push(schema.text(suffix));
    }
    return contents;
}
/**
 * Transform an unknown node.
 */ function unknown(node) {
    throw new Error(`unknown markdown node: ${node.type}`);
}
/**
 * Trim spaces and tabs at the start of `value`.
 *
 * @param {string} value
 *   Value to trim.
 * @returns {string}
 *   Result.
 */ function trimMarkdownSpaceStart(value) {
    let index = 0;
    let code = value.charCodeAt(index);
    while(code === 9 || code === 32){
        index++;
        code = value.charCodeAt(index);
    }
    return value.slice(index);
}
// Return nothing for nodes that are ignored.
function ignore() {
    return undefined;
}
/**
 * Fail when a non-node is found in the tree.
 */ function invalid(node) {
    throw new Error("Expected node, not `" + JSON.stringify(node) + "`");
}
function hastElementHandle(handlers, node, parent, state) {
    const zwitcher = zwitch("tagName", {
        handlers
    });
    return zwitcher(node, parent, state);
}
function handle(schema, handlers, htmlHandlers, node, parent, state) {
    const zwitcher = zwitch("type", {
        invalid,
        unknown,
        handlers: {
            toml: ignore,
            yaml: ignore,
            definition: ignore,
            footnoteDefinition: ignore,
            root (node, _, state) {
                const children = state.all(node);
                const result = schema.topNodeType.createAndFill(null, children);
                return result;
            },
            html (node) {
                const hastRoot = fromHtml(node.value);
                const html = hastRoot.children[0];
                if (html?.type !== "element") return null;
                const [, body] = html.children;
                if (body?.type !== "element") return null;
                const hastElement = body.children[0];
                if (hastElement?.type !== "element") return null;
                const result = hastElementHandle(htmlHandlers, hastElement, undefined, state);
                if (result) return result;
                if (!node.value) return null;
                return schema.text(node.value);
            },
            text (node) {
                const result = schema.text(replaceNewlines(trimLines(String(node.value))));
                return result;
            },
            ...handlers,
            linkReference (node, parent, state) {
                if (handlers.linkReference) return handlers.linkReference(node, parent, state);
                const id = String(node.identifier).toUpperCase();
                const definition = state.definitionById.get(id);
                if (!definition) return revert(schema, node, state);
                const linkNode = {
                    type: "link",
                    url: definition.url,
                    title: definition.title,
                    children: node.children,
                    data: {
                        ...definition.data,
                        ...node.data
                    }
                };
                return handlers.link?.(linkNode, parent, state);
            }
        }
    });
    return zwitcher(node, parent, state);
}
export function toPmNode(nodeType, getAttrs) {
    return (node, _, state)=>{
        const children = state.all(node);
        const result = nodeType.createAndFill(getAttrs?.(node) ?? null, children);
        return result;
    };
}
export function toPmMark(markType, getAttrs) {
    return (node, _, state)=>{
        const children = state.all(node);
        const mark = markType.create(getAttrs?.(node) ?? null);
        return children.map((child)=>child.mark(mark.addToSet(child.marks)));
    };
}
export const toProseMirror = function(tree, options) {
    const doc = handle(options.schema, options.handlers, options.htmlHandlers ?? {}, tree, undefined, createState(options.schema, options.handlers, options.htmlHandlers ?? {}, tree));
    doc.check();
    return doc;
};
